import moment from 'moment-timezone';
import PreferencesService from 'services/api/PreferencesService';
import { HotelConfirmStatus } from 'store/enums/events/HotelConfirmStatus';
import QuestionType from 'store/enums/QuestionType';
import { PreferenceQuestionAnswer, PreferenceQuestionView } from 'store/types/AuraAttribute';
import {
  EventHotelPreferences,
  EventHotelPreferencesData,
  EventHotelPreferencesFormValues,
  EventHotelPreferencesStepConfigItem,
  EventHotelRoom,
  EventHotelRoomView,
  EventRegistrationHotelPreferences,
} from 'store/types/events/EventHotelPreferences';
import { CurrentUser } from 'store/types/User';
import { getUserFullName } from 'util/Format';
import {
  getDefaultPreferencesFormValues,
  getPreferencesAnswersData,
  getPreferencesQuestionsView,
  PREFERENCES_FIELD_NAME,
} from './Preferences';
export const MAX_GUEST_ROOMS = 5;
export const numberOfGuestsConfig: number[] = [1, 2, 3, 4];

export const getStepsConfig = (
  events: EventHotelPreferences[] = [],
  currentUser: CurrentUser
): EventHotelPreferencesStepConfigItem[] =>
  events.map(({ eventTitle, customerName }, index) => ({
    label: `${eventTitle} (${customerName || getUserFullName(currentUser)})`,
    value: index + 1,
    icon: 'LibraryBooks',
  }));

export const getDefaultFormValues = (
  data?: EventHotelPreferences | EventRegistrationHotelPreferences
): EventHotelPreferencesFormValues => {
  const preferencesQuestionsList: PreferenceQuestionView[] = getPreferencesQuestionsView(
    data?.preferences || [],
    PREFERENCES_FIELD_NAME
  ).map((question) => (question.questionType === QuestionType.File ? { ...question, maxFilesCount: 1 } : question));

  return {
    preferencesQuestionsList,
    hotelConfirmStatus:
      data?.hotelRegistration?.rooms?.length || !data?.eventRegistrationId
        ? HotelConfirmStatus.Yes
        : HotelConfirmStatus.No,
    preferences: getDefaultPreferencesFormValues(preferencesQuestionsList),
    rooms:
      data?.hotelRegistration?.rooms?.map((room) => getDefaultRoomValues(room)) ||
      (data?.eventRegistrationId
        ? []
        : [getDefaultRoomValues(undefined, data?.defaultHotelCheckInDate, data?.defaultHotelCheckOutDate)]),
    eventId: data?.eventId || '',
    hotelId: data?.hotelRegistration?.hotelId || '',
    registrantId: data?.registrantId || '',
    guestId: data?.guestId || '',
    userId: data?.userId || '',
    guestName: data?.customerName || '',
    roomTypes: data?.hotelRegistration?.roomTypes || [],
    accessibilities: data?.hotelRegistration?.accessibilities || [],
    hotelName: data?.hotelRegistration?.hotelName || '',
    defaultHotelCheckInDate: data?.defaultHotelCheckInDate,
    defaultHotelCheckOutDate: data?.defaultHotelCheckOutDate,
    isHotelInformationCollected: !!data?.isHotelInformationCollected,
    isPreferencesCollected: !!data?.isPreferencesCollected,
    isHotelInformationCollectedForGuest: !!data?.isHotelInformationCollectedForGuest,
    isPreferencesCollectedForGuest: !!data?.isPreferencesCollectedForGuest,
    eventTitle: data?.eventTitle || '',
    summaryDescription: data?.summaryDescription || '',
    startDate: data?.startDate || '',
    endDate: data?.endDate || '',
    location: data?.location || '',
    address: data?.address,
    timeZoneName: data?.timeZoneName || '',
  };
};

const getNestedValue = (value: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') return undefined;
    return (current as Record<string, unknown>)[key];
  }, value);

export const getEventPreferenceAnswers = (values: EventHotelPreferencesFormValues): PreferenceQuestionAnswer[] =>
  getPreferencesAnswersData(values.preferences, values.preferencesQuestionsList);

export const uploadEventPreferenceFiles = async (
  answers: PreferenceQuestionAnswer[],
  values: EventHotelPreferencesFormValues
): Promise<PreferenceQuestionAnswer[]> => {
  const uploadedAnswers = answers.map((answer) => ({ ...answer }));
  const formFiles = (values as EventHotelPreferencesFormValues & { files?: unknown }).files;
  const fileQuestions = values.preferencesQuestionsList.filter(
    ({ questionType }) => questionType === QuestionType.File
  );

  await Promise.all(
    fileQuestions.map(async (question) => {
      const fileAnswer = uploadedAnswers.find(({ attributeId }) => attributeId === question.id);
      if (!fileAnswer) return;

      const selectedFiles = getNestedValue(formFiles, question.fieldName);
      const files = Array.isArray(selectedFiles) ? selectedFiles : [];
      if (files.length > 1) {
        throw new Error(`Only one file can be uploaded for "${question.name}".`);
      }

      const uploadedFiles = await Promise.all(
        files.map(async (selectedFile: unknown) => {
          if (typeof File !== 'undefined' && selectedFile instanceof File) {
            if (selectedFile.size > 5_000_000) {
              throw new Error(`"${selectedFile.name}" exceeds the 5 MB file size limit.`);
            }
            const uploadedFile = await PreferencesService.uploadFile(selectedFile, question.id);
            return { id: uploadedFile.fileId };
          }

          if (selectedFile && typeof selectedFile === 'object' && 'id' in selectedFile) {
            return { id: String((selectedFile as { id: unknown }).id) };
          }

          return undefined;
        })
      );

      fileAnswer.files = uploadedFiles.filter((uploadedFile): uploadedFile is { id: string } => !!uploadedFile);
      fileAnswer.answer = fileAnswer.files.length ? 'File' : '';
    })
  );

  return uploadedAnswers;
};
export const getDefaultRoomValues = (
  data?: EventHotelRoom,
  defaultHotelCheckInDate = '',
  defaultHotelCheckOutDate = ''
): EventHotelRoomView => ({
  id: data?.id || '',
  checkInDate: data?.id
    ? data?.checkInDate
    : data?.checkInDate || (defaultHotelCheckInDate && moment(defaultHotelCheckInDate).isSameOrAfter(moment()))
    ? defaultHotelCheckInDate
    : '',
  checkOutDate: data?.id
    ? data?.checkOutDate
    : data?.checkOutDate ||
      (defaultHotelCheckOutDate &&
        moment(defaultHotelCheckOutDate).isSameOrAfter(moment()) &&
        moment(defaultHotelCheckOutDate).isAfter(moment(defaultHotelCheckInDate)))
    ? defaultHotelCheckOutDate
    : '',
  roomTypeId: data?.roomTypeId || '',
  numberOfGuests: data?.numberOfGuests || '',
  accessibilityId: data?.accessibilityId || '',
  specialRequest: data?.specialRequest || '',
  hotelRewards: data?.hotelRewards || '',
  guestName: data?.guestName || '',
});

export const getEventsRegistrationData = (data: EventHotelPreferencesFormValues[]): EventHotelPreferencesData[] =>
  data.map(
    ({
      preferencesQuestionsList,
      preferences,
      eventId,
      registrantId,
      rooms,
      hotelId,
      isHotelInformationCollected,
      isHotelInformationCollectedForGuest,
      hotelConfirmStatus,
      guestId,
      uploadedPreferences,
    }) => ({
      eventId,
      hotelId,
      preferences: uploadedPreferences || getPreferencesAnswersData(preferences, preferencesQuestionsList),
      registrantId,
      guestId,
      rooms:
        hotelConfirmStatus === HotelConfirmStatus.Yes
          ? isHotelInformationCollected
            ? guestId
              ? isHotelInformationCollectedForGuest
                ? rooms
                : []
              : rooms
            : []
          : [],
    })
  );
